import { z } from 'zod';

import type { ApiResult } from './client';
import { get, post } from './client';
import { brokerAccountListSchema, brokerAccountSchema } from './_schemas';

import type { BrokerAccount } from '@/types/api';

/**
 * Fleet view — backed by `/admin/broker-accounts` which returns every
 * broker account across every client, denormalised with `client_email` so
 * the dashboard can render one row per account.
 */
export const listBrokerAccounts = (
  signal?: AbortSignal,
): Promise<ApiResult<BrokerAccount[]>> =>
  get('/admin/broker-accounts', brokerAccountListSchema, { signal }) as Promise<ApiResult<BrokerAccount[]>>;

/**
 * OPERATOR-surface account creation (admin auth — NOT the client `/users/me/*`
 * surface). Creates a broker account for any client (multi-account onboarding),
 * audited. `attempts: 1` — a deliberate write.
 */
export interface AdminCreateBrokerAccountBody {
  client_id: string;
  broker_type: string;
  display_name?: string;
  position_mode?: 'netting' | 'hedging';
  base_currency?: string;
  timezone?: string;
  credentials?: Record<string, unknown>;
  /** Private source IP this account's broker connections egress from (broker
   * IP-allowlist grouping). Omit for default routing. */
  egress_ip?: string;
  reason: string;
}

export const adminCreateBrokerAccount = (
  body: AdminCreateBrokerAccountBody,
  signal?: AbortSignal,
): Promise<ApiResult<BrokerAccount>> =>
  post('/admin/broker-accounts', brokerAccountSchema, body, {
    signal, retry: { attempts: 1 },
  }) as Promise<ApiResult<BrokerAccount>>;

// ─── Zerodha daily-token authorize (Kite Connect access_token expires 6 AM IST) ──
const zerodhaLoginUrlSchema = z.object({
  login_url: z.string(),
  has_token: z.boolean(),
  note: z.string(),
});
export type ZerodhaLoginUrl = z.infer<typeof zerodhaLoginUrlSchema>;

export const getZerodhaLoginUrl = (
  accountId: string, signal?: AbortSignal,
): Promise<ApiResult<ZerodhaLoginUrl>> =>
  get(`/admin/broker-accounts/${encodeURIComponent(accountId)}/zerodha/login-url`,
      zerodhaLoginUrlSchema, { signal }) as Promise<ApiResult<ZerodhaLoginUrl>>;

const zerodhaAuthorizeSchema = z.object({
  authorized: z.boolean(),
  user_id: z.string().nullable().optional(),
  login_time: z.string().nullable().optional(),
});
export type ZerodhaAuthorizeResult = z.infer<typeof zerodhaAuthorizeSchema>;

export const zerodhaAuthorize = (
  accountId: string, body: { request_token: string; reason: string },
): Promise<ApiResult<ZerodhaAuthorizeResult>> =>
  post(`/admin/broker-accounts/${encodeURIComponent(accountId)}/zerodha/authorize`,
       zerodhaAuthorizeSchema, body, { retry: { attempts: 1 }, timeoutMs: 30_000 },
  ) as Promise<ApiResult<ZerodhaAuthorizeResult>>;
